import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import {
	Button,
	Text,
	Flex,
	Collapse,
	Box,
	Icon,
	VStack,
	FormLabel,
	HStack,
	Spacer,
	useDisclosure,
} from 'src/components/ui';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
	CommonAttributes,
	IChannelMediaAttributes,
	ICreative,
	ImageLayer,
	isCreatomateAttributes,
	TextLayer,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { renderCreativesByPlacement } from './utils/renderCreativesByPlacement';
import { formatLabel } from './utils/formatInputLabel';
import InputField from './components/InputField';
import { attributeConfig } from './utils/attributesConfig';
import {
	ICampaign,
	IChannelCreativeAttributes,
	ThreadTypesEnum,
} from 'src/lib/schemas';
import { SelectSearchInput } from 'src/components/common/form';
import CustomModal from 'src/components/common/CustomModal';
import { AssistantChatContext, CampaignContext } from 'src/contexts';
import MediaCreativeModal from 'src/components/campaign/DesignOutput/parts/MediaCreativeModal';
import useToggleWithPayload from 'src/hooks/useToggleWithPayload';
import UserContext from 'src/contexts/user/UserContext';
import { getCompanyAccount } from 'src/services/account';
import {
	ICreativePayload,
	IPerview,
} from 'src/components/campaign/DesignOutput/parts/DesignDirections/parts/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { customToast, toastError, toastSuccess } from 'src/services/toast';
import http from 'src/services/http';
import { updateCampaignCreative } from 'src/services/campaign';
import CustomInput from 'src/components/common/CustomInput/CustomInput';
import { useAssistantField } from '../../assistantChat/hooks/useAssistantField';
import MediaInputHook from 'src/components/common/CustomInput/hooks/MediaInputHook';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import Design from 'src/pages/design/Design';
import {
	RIGHT_SIDEBAR_WIDTH_COLLAPSED,
	validTextInputKeys,
} from '../utils/constants';

interface EditCreativeModalProps {
	isOpen: boolean;
	onClose: () => void;
	initialValues: ICreative;
	config?: IChannelCreativeAttributes;
	handleRefetchCreatives: () => void;
}

const EditCreativeModal: React.FC<EditCreativeModalProps> = ({
	isOpen,
	onClose,
	initialValues,
	config,
	handleRefetchCreatives,
}) => {
	const { id: campaignId, campaign, setCampaign, isEditorMode, setIsEditorMode } = useContext(CampaignContext);
	const [editedCreative, setEditedCreative] =
		useState<ICreative>(initialValues);
	const [designerMode, setDesignerMode] = useState(false);
	const initialHeadline =
		(initialValues.attributes as any).headlines?.[0] || '';
	const initialDescription =
		(initialValues.attributes as any).descriptions?.[0] || '';
	const [selectedHeadline, setSelectedHeadline] =
		useState<string>(initialHeadline);
	const [selectedDescription, setSelectedDescription] =
		useState<string>(initialDescription);
	const [collapsedGroups, setCollapsedGroups] = useState<
		Record<string, boolean>
	>({});
	const { closeChat } = useContext(AssistantChatContext);
	const { user } = useContext(UserContext);
	const [fonts, setFonts] = useState<string[]>([]);
	const [isChangingImageLayer, setIsChangingImageLayer] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isEditingCreatomate, setIsEditingCreatomate] = useState(false);
	const creativeFormToggle = useToggleWithPayload<ICreative>(editedCreative);
	const [previewPSDMeta, setPreviewPSDMeta] = useState<IPerview | undefined>(
		undefined,
	);
	const fields =
		(editedCreative as any).attributes.output ?? editedCreative.attributes;
	const firstInputRef = useRef<HTMLInputElement | null>(null);
	const {
		isOpen: isConfirmDialogOpen,
		onOpen: onOpenConfirmDialog,
		onClose: onCloseConfirmDialog,
	} = useDisclosure();

	const avoidImageChanges = true; //This is only for this version

	useEffect(() => {
		if (firstInputRef.current) {
			firstInputRef.current.focus();
		}
	}, []);

	const getLayersWithParents = (type: string, layers?: any) => {
		const layersCopy = [...(layers ?? fields.image?.layers ?? [])];

		return layersCopy
			.filter((layer) => layer.type === type && layer.primary)
			.map((layer) => {
				const parents = [];
				let currentLayer = layer;

				while (currentLayer?.parentName) {
					const parentLayer = layersCopy.find(
						(l) => l.name === currentLayer.parentName,
					);
					if (parentLayer) parents.unshift(currentLayer.parentName);
					currentLayer = parentLayer;
				}

				return { ...layer, parents };
			});
	};
	const [textLayersContent, setTextLayersContent] = useState<TextLayer[]>(
		getLayersWithParents('text'),
	);
	const [mediaLayersContent, setMediaLayersContent] = useState<ImageLayer[]>(
		getLayersWithParents('image'),
	);

	const mediaContent =
		editedCreative?.attributes as unknown as IChannelMediaAttributes;

	useEffect(() => {
		if (isSaving) {
			try {
				handleEditCreative(editedCreative);
			} catch (error) {
				toastError(error);
				setIsSaving(false);
			}
		}
	}, [isSaving]);
	const handleEditCreative = async (creative: any): Promise<void> => {
		try {
			if (editedCreative) {
				const payload: ICreativePayload = {
					creative: editedCreative,
					psdPerview: previewPSDMeta
						? {
								flatFile: previewPSDMeta.flatFile,
								layeredFile: previewPSDMeta.layeredFile,
								layers: [...textLayersContent, ...mediaLayersContent],
							}
						: undefined,
					text: textLayersContent,
					image: mediaLayersContent,
					actions: {
						action: 'changeDD',
						generateCreatives: false,
						editMode: false,
						changeDD: true,
					},
				};
				changeTextLayer(payload, creative);
			} else {
				setEditedCreative((prev) => {
					if (prev?.id === creative.id) return prev;
					return creative;
				});
				setIsSaving(false);
			}
		} catch (error) {
			console.error(error);
			setIsSaving(false);
		}
	};

	const handleChangeMediaLayer = (
		flatFile: string,
		layeredFile: string,
		layers: (ImageLayer | TextLayer)[],
	) => {
		if (isChangingImageLayer) {
			return;
		}

		setPreviewPSDMeta((prevState) => ({
			...prevState,
			flatFile,
			layeredFile,
			layers,
		}));
		const newMediaLayersContent = getLayersWithParents('image', layers);
		setMediaLayersContent(newMediaLayersContent);
	};

	const changeTextLayer = async (
		payload: ICreativePayload,
		creative: ICreative,
	) => {
		if ((!payload.psdPerview && campaignId) || avoidImageChanges) {
			const newFields = {
				...fields,
				...((creative as any).attributes?.output ??
					creative.attributes ??
					creative),
			};
			const updatePayload = { attributes: { ...newFields } };

			if (!campaign?.id) return;
			await updateCampaignCreative(
				campaign.id,
				editedCreative.id,
				updatePayload,
			);
			handleRefetchCreatives();
			onClose();
			return;
		}

		const textValues = payload.text.map((layer) => ({
			value: layer.content,
			layer: layer.name,
			visible: layer.visible,
			property: 'text',
		}));
		const imageValues = payload.image?.map((layer) => ({
			value: layer.imageUrl,
			layer: layer.name,
			visible: layer.visible,
			property: 'image',
		}));

		const values = [...textValues, ...(imageValues ?? [])];

		const changeLayerPayload = {
			inputs: {
				layeredFile:
					payload.psdPerview?.layeredFile ??
					(editedCreative as any).attributes.layeredFile,
				values,
			},
		};

		try {
			const { data } = await http.post(
				'/v2/apps/fusion_ai.edit_psd/execute/edit_psd',
				changeLayerPayload,
			);
			if (data) await processCallback(data.callback, payload, creative);
		} catch (error) {
			toastError(error);
			setIsSaving(false);
		}
	};

	const updateCreative = async (payload: IPerview, creative: ICreative) => {
		const attributes =
			creative?.attributes as unknown as IChannelMediaAttributes;
		if (!attributes || !campaign?.id) return;

		const updatePayload = {
			attributes: {
				...attributes,
				image: {
					...attributes.image,
					layeredFile: payload.layeredFile,
					flatFile: payload.flatFile,
					layers: payload.layers,
				},
			},
		};

		await updateCampaignCreative(campaign.id, creative.id, updatePayload);
		handleRefetchCreatives();
		onClose();
	};

	const processCallback = async (
		callbackUrl: string,
		payload: ICreativePayload,
		creative?: ICreative,
	) => {
		try {
			const response = await http.get(callbackUrl);
			const { status, body } = response.data;
			if (status === 'processing' || status === 'pending') {
				setTimeout(
					processCallback.bind(null, callbackUrl, payload, creative),
					500,
				);
			} else if (status === 'error' || status === 'failed') {
				customToast('Error processing image', 'error');
				setIsSaving(false);
			} else if (status === 'successful') {
				const updatedTextLayers = textLayersContent.map((layer) => {
					return {
						...layer,
						content:
							payload.text.find((l) => l.name === layer.name)?.content ??
							layer.content,
					};
				});
				const updatedMediaLayers = mediaLayersContent.map(
					(layer: ImageLayer) => {
						return {
							...layer,
							content:
								payload.image?.find((l) => l.name === layer.name)?.imageUrl ??
								layer.imageUrl,
						};
					},
				);

				const layers = [...updatedTextLayers, ...updatedMediaLayers];

				setPreviewPSDMeta((prevState) => ({
					...prevState,
					flatFile: body.flatFile,
					layeredFile: body.layeredFile,
					layers,
				}));
				if (isSaving) {
					await updateCreative(
						{ flatFile: body.flatFile, layeredFile: body.layeredFile, layers },
						payload.creative,
					);
					toastSuccess('Your changes have been saved');
					setIsSaving(false);
					onClose();
				}

				payload.actions.changeDD &&
					creative &&
					setEditedCreative((prev) => {
						if (prev?.id === creative.id) return prev;
						return creative;
					});
			}
		} catch (e) {
			console.error({ e });
			toastError(e);
			setIsSaving(false);
		}
	};

	useEffect(() => {
		const image = mediaContent?.image || {};

		if (editedCreative && image && image.layeredFile) {
			setPreviewPSDMeta((prevState) => ({
				...prevState,
				flatFile: image.flatFile,
				layeredFile: image.layeredFile,
				layers: image.layers,
			}));
		}
	}, [mediaContent?.image?.layeredFile]);

	useEffect(() => {
		getGlobalFont().then(
			(font) => font && setFonts((prevState) => [...prevState, font]),
		);
	}, []);

	useEffect(() => {
		if (!designerMode) return;
		if (isCreatomateAttributes(editedCreative.attributes)) {
			setIsEditingCreatomate(true);
			setIsEditorMode(true); // Enable editor mode globally
		} else {
			creativeFormToggle.onOpen(editedCreative);
		}
	}, [designerMode, setIsEditorMode]);

	const assistant = useAssistantField();

	const handleOpenAssistant = (layer: any) =>
		assistant.openAssistant({
			type: ThreadTypesEnum.Campaign,
			reference: campaignId ?? '',
			fieldName: layer.name,
			control,
			variant: editedCreative.variant,
			channel: editedCreative.channel,
			displayName: layer.displayName,
			value: layer.content,
			setValue: (newValue) => setFieldValue(layer.name, newValue),
		});

	const getGlobalFont = async () => {
		if (!user) return;
		const { brandIdentity } = await getCompanyAccount(user.account);
		return brandIdentity?.fontType;
	};

	const handleCloseEditModal = () => {
		closeChat();
		onClose();
	};

	const handleCloseDesignerMode = () => {
		creativeFormToggle.onClose();
		setDesignerMode(false);
		setIsEditorMode(false); // Disable editor mode globally
		handleCloseEditModal();
	};
	// Find CTA options from config - try multiple matching strategies
	const findCTAOptions = () => {
		// DEBUG: Log what we receive
		console.log('=== CTA Debug ===');
		console.log('config:', config);
		console.log('config?.creatives:', config?.creatives);
		console.log('initialValues.app:', initialValues.app);
		console.log('initialValues.name:', initialValues.name);
		console.log('initialValues.channel:', initialValues.channel);
		
		if (!config?.creatives || config.creatives.length === 0) {
			console.log('No config.creatives found!');
			return [];
		}
		
		// Try to find by app first
		let creativeConfig = config.creatives.find((c) => c.app === initialValues.app);
		console.log('Found by app:', creativeConfig?.name);
		
		// If not found by app, try by name or mediaType
		if (!creativeConfig) {
			creativeConfig = config.creatives.find(
				(c) => c.name === initialValues.name || c.mediaType === initialValues.mediaType
			);
			console.log('Found by name/mediaType:', creativeConfig?.name);
		}
		
		// If still not found, try the first creative that has callToAction options
		if (!creativeConfig) {
			creativeConfig = config.creatives.find((c) => 
				c.attributes?.some((att) => att.property === 'callToAction' && att.options && att.options.length > 0)
			);
			console.log('Found by callToAction attribute:', creativeConfig?.name);
		}
		
		// Last resort: use first creative's callToAction
		if (!creativeConfig) {
			creativeConfig = config.creatives[0];
			console.log('Using first creative:', creativeConfig?.name);
		}
		
		const ctaAttribute = creativeConfig?.attributes?.find((att) => att.property === 'callToAction');
		console.log('CTA attribute found:', ctaAttribute);
		console.log('CTA options:', ctaAttribute?.options);
		return ctaAttribute?.options ?? [];
	};
	
	const CTAOptions = findCTAOptions();
	console.log('Final CTAOptions:', CTAOptions);

	const handleChangeTextLayerContent = useCallback(() => {
		document.getElementById('change-text-layer-button-preview')?.click();
	}, []);

	const handleChange = (value: string, field: string) => {
		setEditedCreative((prevState) => {
			if (field.includes('[')) {
				const [key, index] = field.split(/\[|\]/).filter(Boolean);
				const arrayField = (prevState as any).attributes[
					key as keyof CommonAttributes
				];

				if (Array.isArray(arrayField)) {
					if (field.toLowerCase().includes('headline')) {
						setSelectedHeadline(value);
					}
					if (field.toLowerCase().includes('description')) {
						setSelectedDescription(value);
					}

					const updatedArray = [...arrayField];
					updatedArray[parseInt(index)] = value;

					return {
						...prevState,
						attributes: { ...prevState.attributes, [key]: updatedArray },
					};
				}
			}

			return {
				...prevState,
				attributes: {
					...prevState.attributes,
					[field as keyof CommonAttributes]: value,
				},
			};
		});
	};

	const toggleCollapse = (key: string) => {
		setCollapsedGroups((prevState) => ({
			...prevState,
			[key]: !prevState[key],
		}));
	};

	const renderInputField = (
		key: string,
		value: any,
		config: any,
		maxLength?: number,
	) => {
		console.log('renderInputField called with key:', key, 'config.inputType:', config.inputType, 'CTAOptions.length:', CTAOptions?.length);
		
		if (config.inputType === 'dropdown' && CTAOptions && CTAOptions.length > 0) {
			// Map options with id as value and display text as label
			const ctaOptionsMapped = CTAOptions.map((opt) => ({
				label: opt.value,
				value: opt.id,
			}));
			
			console.log('Rendering CTA dropdown with options:', ctaOptionsMapped);
			
			// Get current CTA value from creative attributes
			const currentCtaValue = (editedCreative.attributes as any)[key];
			console.log('Current CTA value:', currentCtaValue);
			
			// Find matching option - check both by id (value) and by display text (label)
			// since the creative might store either format
			// Use case-insensitive comparison and trim whitespace for robustness
			const normalizedCurrentValue = currentCtaValue?.toString().toLowerCase().trim();
			const selectedOption = ctaOptionsMapped.find(
				(opt) => 
					opt.value.toLowerCase().trim() === normalizedCurrentValue || 
					opt.label.toLowerCase().trim() === normalizedCurrentValue
			);
			console.log('Selected option:', selectedOption);
			
			return (
				<Box key={key}>
					<FormLabel>{config.label}</FormLabel>
					<SelectSearchInput
						name={key}
						placeholder="Select/search"
						options={ctaOptionsMapped}
						value={selectedOption?.value || ''}
						onChange={(newValue) => {
							console.log('CTA onChange:', newValue);
							// Store the id (value) in the creative attributes
							handleChange(String(newValue), key);
						}}
					/>
				</Box>
			);
		}
		return (
			<InputField
				key={key}
				label={config.label}
				value={String(value)}
				onChange={(value) => handleChange(value, key)}
				isTextarea={config.inputType === 'textarea'}
				maxLength={maxLength}
				withAssistant
				name={key}
				creativeData={initialValues}
			/>
		);
	};

	const formMethods = useForm({
		defaultValues: textLayersContent.reduce(
			(acc: Record<string, string>, layer) => {
				acc[layer.name] = layer.content;
				return acc;
			},
			{},
		),
	});

	useEffect(() => {
		formMethods.reset(
			textLayersContent.reduce((acc: Record<string, string>, layer) => {
				acc[layer.name] = layer.content;
				return acc;
			}, {}),
		);
	}, [textLayersContent, formMethods]);

	const handleTextLayersChange = useCallback(() => {
		handleChangeTextLayerContent();
	}, [formMethods, textLayersContent, handleChangeTextLayerContent]);

	const setFieldValue = useCallback(
		(field: string, value: string) => {
			setTextLayersContent((prevLayers) =>
				prevLayers.map((layer) =>
					layer.name === field ? { ...layer, content: value } : layer,
				),
			);
			formMethods.setValue(field, value);
			handleTextLayersChange();
		},
		[formMethods, handleTextLayersChange],
	);

	const { control } = formMethods;

	const toggleShowMediaLayer = (layerId: number) => {
		setMediaLayersContent((prevState) =>
			prevState.map((layer) =>
				layer.id === layerId ? { ...layer, visible: !layer.visible } : layer,
			),
		);
	};

	const toggleShowTextLayer = (layerId: string) => {
		setTextLayersContent((prevState) =>
			prevState.map((layer) =>
				layer.id === layerId ? { ...layer, visible: !layer.visible } : layer,
			),
		);
	};

	const renderInputsBasedOnAttributes = (
		validationConfig?: IChannelCreativeAttributes,
	) => {
		const nonImageFields = Object.keys(fields)
			.filter((key) => {
				if (
					[
						'generate_ig_story_ad_creatomate',
						'generate_fb_story_ad_creatomate',
					].includes(initialValues.app ?? '')
				) {
					return key === 'callToAction';
				}
				return validTextInputKeys.includes(key);
			})
			.map((key, index) => {
				const attribute = validationConfig?.creatives[0].attributes.find(
					(att) => att.property === key,
				);

				const maxLength = attribute?.validations.find(
					(validation) => validation.id === 'String.maxLength',
				)?.value;

				const maxLengthNumber =
					typeof maxLength === 'number' ? maxLength : undefined;

				const value = (editedCreative as any).attributes[
					key as keyof CommonAttributes
				];
				const config = attributeConfig[key as keyof typeof attributeConfig] || {
					label: formatLabel(key),
					inputType: 'string',
				};

				// const openAssistant = (layer: any) =>
				// 	useAssistantField({
				// 		type: ThreadTypesEnum.Campaign,
				// 		reference: campaignId ?? '',
				// 		fieldName: layer.name,
				// 		control,
				// 		variant: editedCreative.variant,
				// 		channel: editedCreative.channel,
				// 		displayName: layer.displayName,
				// 	});

				if (Array.isArray(value)) {
					return (
						<Box
							key={key}
							p={2}
							borderRadius="md"
							onClick={() => toggleCollapse(key)}
							cursor="pointer"
						>
							<Flex justifyContent="space-between">
								<FormLabel mb={0}>{config.label}</FormLabel>
								<Icon
									boxSize={5}
									aria-label={`Toggle ${key}`}
									as={collapsedGroups[key] ? ChevronDownIcon : ChevronUpIcon}
								/>
							</Flex>
							<Collapse in={!collapsedGroups[key]} animateOpacity>
								<VStack mt={2} align="stretch">
									{value.map((item, index) => {
										return (
											<InputField
												key={`${key}-${index}`}
												value={item}
												onChange={(value) =>
													handleChange(value, `${key}[${index}]`)
												}
												maxLength={maxLengthNumber}
												withAssistant
												name={`${key}[${index}]`}
												creativeData={initialValues}
											/>
											// <CustomInput
											// 	key={item.id}
											// 	ref={index === 0 ? firstInputRef : null}
											// 	control={formMethods.control}
											// 	label={item.displayName}
											// 	name={item.name}
											// 	isLayerVisible={item.visible}
											// 	onCopilotClick={() => openAssistant(item)}
											// 	onChange={(value) => setFieldValue(item.name, value)}
											// />
										);
									})}
								</VStack>
							</Collapse>
						</Box>
					);
				}

				return renderInputField(key, value, config, maxLengthNumber);
			});
		const imageField = Object.keys(fields).includes('image')
			? (() => {
					return (
						<FormProvider key="image" {...formMethods}>
							<Text size="md" fontWeight="bold" mt={5}>
								Image
							</Text>
							<VStack spacing={6} align="stretch">
								{textLayersContent.map((layer, index) => {
									return (
										<CustomInput
											key={layer.id}
											control={formMethods.control}
											label={layer.displayName}
											name={layer.name}
											isLayerVisible={layer.visible}
											onCopilotClick={() => handleOpenAssistant(layer)}
											onShowClick={() => toggleShowTextLayer(layer.id)}
											onChange={(value) => setFieldValue(layer.name, value)}
										/>
									);
								})}
								{previewPSDMeta &&
									mediaLayersContent.map((layer) => {
										return (
											<MediaInputHook
												key={layer.id}
												control={formMethods.control}
												onShowClick={() => toggleShowMediaLayer(layer.id!)}
												onChangePreviewPSD={handleChangeMediaLayer}
												previewPSD={previewPSDMeta}
												layer={layer}
												onChangingLayer={setIsChangingImageLayer}
											/>
										);
									})}
							</VStack>
						</FormProvider>
					);
				})()
			: null;

		return (
			<>
				{nonImageFields}
				{/* {imageField} */}
			</>
		);
	};

	// Check if this creative shouldn't have designer mode (SMS, Email, Twitter, Video)
	const isDirectMarketingCreative = initialValues?.placements?.some(
		(placement) =>
			placement === 'smsDirectMarketing' ||
			placement === 'emailDirectMarketing' ||
			placement === 'twitterSocialMedia' ||
			placement.includes('Video')
	) || ['sms', 'email', 'twitter'].includes(initialValues?.channel?.toLowerCase() || '');

	const modalHeader = (
		<HStack pr={10}>
			<Text size="md">
				<Text as="span" size="md" fontWeight="bold">
					Editing:{' '}
				</Text>
				Option {initialValues?.variant} - {initialValues?.name}
			</Text>
			<Spacer />
			{!isDirectMarketingCreative && (
				<Button
					size="sm"
					variant="blankOutline"
					onClick={() => setDesignerMode(true)}
				>
					Switch to designer mode
				</Button>
			)}
		</HStack>
	);

	const modalFooter = (
		<Flex
			direction={{ base: 'column-reverse', md: 'row' }}
			gap={{ base: 3, md: 3 }}
			w="full"
			justify={{ base: 'stretch', md: 'flex-end' }}
		>
			<Button onClick={handleCloseEditModal} w={{ base: 'full', md: 'auto' }}>
				Close
			</Button>
			<Button
				isLoading={isSaving}
				onClick={() => setIsSaving(true)}
				variant="orangeSolid"
				w={{ base: 'full', md: 'auto' }}
			>
				Save changes
			</Button>
		</Flex>
	);

	if (isEditingCreatomate)
		return (
			<>
				<ConfirmDialog
					isOpen={isConfirmDialogOpen}
					onClose={onCloseConfirmDialog}
					onConfirm={() => {
						setIsEditingCreatomate(false);
						setIsEditorMode(false); // Disable editor mode globally
						onCloseConfirmDialog();
					}}
					message="You have unsaved changed that will be lost if you decide to continue. Are you sure you want to leave this page?"
				/>
				<div
					style={{
						left: 0,
						top: 72,
						width: `calc(100vw - ${RIGHT_SIDEBAR_WIDTH_COLLAPSED})`,
						height: '100vh',
						zIndex: 1000,
						position: 'fixed',
					}}
				>
					<Design
						fonts={[]}
						template={editedCreative!.attributes}
						onClose={() => {
							setDesignerMode(false);
							setIsEditingCreatomate(false);
							setIsEditorMode(false); // Disable editor mode globally
						}}
						onSave={handleEditCreative}
						onChange={() => {}}
					/>
				</div>
			</>
		);
	return (
		<CustomModal
			isOpen={isOpen}
			onClose={handleCloseEditModal}
			size="4xl"
			maxWidth={{ base: '95vw', md: '4xl' }}
			header={modalHeader}
			footer={modalFooter}
			trapFocus={false}
		>
			{designerMode && previewPSDMeta && creativeFormToggle.payload ? (
				<MediaCreativeModal
					isOpen={creativeFormToggle.isOpen}
					data={creativeFormToggle.payload}
					onClose={handleCloseDesignerMode}
					onRefetchCreatives={handleRefetchCreatives}
					layeredFile={previewPSDMeta.layeredFile}
					textLayersContent={textLayersContent}
					fonts={fonts}
					closeEditModal={handleCloseEditModal}
					isCreative
				/>
			) : (
				<Flex gap={{ base: 3, md: 5 }} maxH="80vh" direction={{ base: 'column', md: 'row' }}>
					<Box
						flex={1}
						overflowY="auto"
						pr={{ base: 0, md: 4 }}
						sx={{
							'&::-webkit-scrollbar': { width: '10px' },
							'&::-webkit-scrollbar-track': {
								background: '#f1f1f1',
								borderRadius: '8px',
							},
							'&::-webkit-scrollbar-thumb': {
								background: '#888',
								borderRadius: '8px',
							},
							'&::-webkit-scrollbar-thumb:hover': { background: '#555' },
						}}
					>
						<VStack spacing={6} align="stretch">
							{renderInputsBasedOnAttributes(config)}
						</VStack>
					</Box>
					<Flex flex={1} justifyContent={{ base: 'center', md: 'flex-end' }}>
						{renderCreativesByPlacement({
							creatives: [editedCreative],
							withActions: false,
							size: 'sm',
							selectedHeadline,
							selectedDescription,
							layeredFile: previewPSDMeta?.layeredFile,
							textLayersContent,
							mediaLayersContent,
							fonts,
							designDirection: editedCreative,
							isChangingLayer: isChangingImageLayer,
							editMode: true,
							onRefetchCreatives: handleRefetchCreatives,
						})}
					</Flex>
				</Flex>
			)}
		</CustomModal>
	);
};

export default EditCreativeModal;
